import React, { useState, useRef, useEffect } from 'react';
import { MapPin, AlertTriangle, Frown, Landmark, Lock, Flag, Info, CheckCircle, ChevronRight, Loader2 } from 'lucide-react';
import { trackCalculatorStart, trackCalculatorResult } from '../utils/analytics';

/* ─── Constants ─── */
const API_BASE = process.env.REACT_APP_API_BASE || 'https://housing-external.onrender.com';

const STEPS = ['location', 'income', 'details', 'results'];

const DEDUCTIONS = {
  dependentDeduction: 480,
  elderlyDisabledDeduction: 525,
  medicalThresholdPct: 0.03,
  ttpPctAdjusted: 0.30,
  ttpPctGross: 0.10,
  defaultMinimumRent: 50,
};

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtMonthly = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

function incomeBracket(income) {
  if (income < 20000) return 'under_20k';
  if (income < 40000) return '20k_40k';
  if (income < 60000) return '40k_60k';
  if (income < 80000) return '60k_80k';
  return '80k_plus';
}

/* ─── Main Component ─── */
export default function CalculatorPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Location
  const [zip, setZip] = useState('');
  const [areaData, setAreaData] = useState(null);

  // Step 2: Income + household
  const [householdSize, setHouseholdSize] = useState(1);
  const [annualIncome, setAnnualIncome] = useState('');

  // Eligibility results (computed between step 2→3)
  const [eligiblePrograms, setEligiblePrograms] = useState([]);
  // State programs that match on income but are referral-only / for a specific
  // population. Shown separately, never as "you qualify".
  const [referralPrograms, setReferralPrograms] = useState([]);
  

  // Step 3: Deduction details
  const [dependents, setDependents] = useState(0);
  const [isElderlyDisabled, setIsElderlyDisabled] = useState(false);
  const [childcare, setChildcare] = useState('');
  const [medicalExp, setMedicalExp] = useState('');
  const [disabilityExp, setDisabilityExp] = useState('');

  // Step 4: Results
  const [result, setResult] = useState(null);
  const [nearbyPHAs, setNearbyPHAs] = useState([]);
  const [phaLoading, setPhaLoading] = useState(false);

  const topRef = useRef(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [step]);

  /* ── Step 1: Look up ZIP ── */
  const lookupZip = async () => {
    if (!/^\d{5}$/.test(zip)) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/calculator/lookup?zip=${zip}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'ZIP code not found. Please check and try again.');
        return;
      }
      setAreaData(data);
      trackCalculatorStart(zip);
      setStep(1);
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2 → 3: Check eligibility ── */
  const checkEligibility = () => {
    const income = parseFloat(annualIncome.replace(/[,$\s]/g, '')) || 0;
    if (income <= 0) {
      setError('Please enter your annual household income.');
      return;
    }
    setError(null);

    const size = Math.min(Math.max(householdSize, 1), 8);
    const limits = areaData.incomeLimits;
    const programs = [];

    // Federal programs
    if (limits.low && income <= limits.low[size]) {
      programs.push({ key: 'publicHousing', name: 'Public Housing', tier: '80% AMI' });
    }
    if (limits.veryLow && income <= limits.veryLow[size]) {
      programs.push({ key: 'section8', name: 'Section 8 / Housing Choice Voucher', tier: '50% AMI' });
    }

    // State programs. Tier 1 = anyone income-eligible can apply, so it counts
    // toward "you may qualify". Tier 2 / referral-only programs serve a specific
    // population (e.g. people leaving institutions) and are listed separately.
    const referrals = [];
    if (areaData.statePrograms) {
      for (const sp of areaData.statePrograms) {
        const tierKey = sp.amiThreshold <= 30 ? 'extremelyLow' : sp.amiThreshold <= 50 ? 'veryLow' : 'low';
        if (!(limits[tierKey] && income <= limits[tierKey][size])) continue;
        const entry = {
          key: sp.key, name: sp.name, tier: `${sp.amiThreshold}% AMI`, applyUrl: sp.applyUrl,
          waitlistStatus: sp.waitlistStatus, targetPopulation: sp.targetPopulation, description: sp.description,
        };
        if (sp.referralOnly || sp.tier === 2) referrals.push(entry);
        else programs.push(entry);
      }
    }

    setEligiblePrograms(programs);
    setReferralPrograms(referrals);
    setStep(programs.length > 0 ? 2 : 3); // skip details if not eligible, go straight to results
    
    if (programs.length === 0) {
      setResult({ notEligible: true });
      trackCalculatorResult([], 0);
    }
  };

  /* ── Step 3 → 4: Calculate TTP ── */
  const calculateTTP = () => {
    const grossAnnual = parseFloat(annualIncome.replace(/[,$\s]/g, '')) || 0;
    const childcareAmt = parseFloat(childcare.replace(/[,$\s]/g, '')) || 0;
    const medicalAmt = parseFloat(medicalExp.replace(/[,$\s]/g, '')) || 0;
    const disabilityAmt = parseFloat(disabilityExp.replace(/[,$\s]/g, '')) || 0;

    // Deductions
    let totalDeductions = 0;
    totalDeductions += dependents * DEDUCTIONS.dependentDeduction;
    if (isElderlyDisabled) totalDeductions += DEDUCTIONS.elderlyDisabledDeduction;
    totalDeductions += childcareAmt;
    if (isElderlyDisabled && medicalAmt > 0) {
      totalDeductions += Math.max(0, medicalAmt - (grossAnnual * DEDUCTIONS.medicalThresholdPct));
    }
    totalDeductions += disabilityAmt;

    const adjustedAnnual = Math.max(0, grossAnnual - totalDeductions);
    const monthlyAdjusted = adjustedAnnual / 12;
    const monthlyGross = grossAnnual / 12;

    const ttp = Math.max(
      monthlyAdjusted * DEDUCTIONS.ttpPctAdjusted,
      monthlyGross * DEDUCTIONS.ttpPctGross,
      DEDUCTIONS.defaultMinimumRent
    );

    const finalResult = {
      notEligible: false,
      grossAnnual,
      totalDeductions,
      adjustedAnnual,
      monthlyAdjusted: Math.round(monthlyAdjusted),
      monthlyGross: Math.round(monthlyGross),
      ttp: Math.round(ttp),
      programs: eligiblePrograms,
    };

    setResult(finalResult);
    trackCalculatorResult(finalResult.programs?.map(p => p.name), finalResult.ttp);
    setStep(3);

    // Fetch nearby PHAs
    setPhaLoading(true);
    fetch(`${API_BASE}/api/calculator/nearby-phas?zip=${zip}&limit=5`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.phas) setNearbyPHAs(data.phas); })
      .catch(() => {})
      .finally(() => setPhaLoading(false));

    // Log analytics (fire and forget)
    try {
      fetch(`${API_BASE}/api/calculator/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zip,
          state: areaData?.state,
          fmrArea: areaData?.fmrArea,
          householdSize,
          incomeBracket: incomeBracket(grossAnnual),
          programsEligible: eligiblePrograms.map(p => p.key),
          estimatedTtp: Math.round(ttp),
          referrer: document.referrer || window.location.href,
        })
      }).catch(() => {});
    } catch (e) {}
  };

  /* ── Reset ── */
  const reset = () => {
    setStep(0);
    setZip('');
    setAreaData(null);
    setAnnualIncome('');
    setHouseholdSize(1);
    setEligiblePrograms([]);
    setReferralPrograms([]);
    setDependents(0);
    setIsElderlyDisabled(false);
    setChildcare('');
    setMedicalExp('');
    setDisabilityExp('');
    setResult(null);
    setError(null);
    setNearbyPHAs([]);
    setPhaLoading(false);
  };

  /* ─────────────────────────────────── */
  /*           RENDER                    */
  /* ─────────────────────────────────── */
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div ref={topRef} />

      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">

            {/* Page heading */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-4 py-1.5 text-sm text-blue-700 mb-5">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                Free • No account required • Updated for {areaData?.fiscalYear || 'FY2026'} HUD income limits
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                Housing Assistance Eligibility & Rent Calculator
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-1">
                Check if you qualify for Public Housing, Section 8, or state rental assistance programs — and estimate your monthly rent.
              </p>
              <p className="text-sm text-gray-400">
                Works for any address in the United States. Powered by official HUD income limits.
              </p>
            </div>

            {/* Wizard card — centered narrower */}
            <div className="max-w-2xl mx-auto">

              {/* Step indicators */}
              <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-6">
          {['Location', 'Income', 'Details', 'Results'].map((label, i) => (
            <React.Fragment key={label}>
              {i > 0 && <div className={`h-px w-5 sm:w-8 ${i <= step ? 'bg-blue-400' : 'bg-slate-200'}`} />}
              <div className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
                  ${i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-white text-slate-400 border border-slate-200'}`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${i <= step ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          
          {/* Error banner */}
          {error && (
            <div className="bg-red-50 border-b border-red-100 px-6 py-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
            </div>
          )}

          <div className="p-6 sm:p-8">

            {/* ── STEP 0: Location ── */}
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800 mb-1">Where do you plan to live?</h2>
                  <p className="text-sm text-slate-500">Enter the ZIP code where you'd use your housing assistance. Income limits vary by area.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">ZIP Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    className="w-full px-4 py-3.5 text-lg border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                    placeholder="e.g. 02101"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    onKeyDown={(e) => e.key === 'Enter' && lookupZip()}
                    autoFocus
                  />
                </div>

                <button
                  onClick={lookupZip}
                  disabled={zip.length !== 5 || loading}
                  className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Looking up...
                    </span>
                  ) : 'Look Up My Area →'}
                </button>
              </div>
            )}

            {/* ── STEP 1: Income ── */}
            {step === 1 && areaData && (
              <div className="space-y-6">
                {/* Area found badge */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">{areaData.county}, {areaData.state}</p>
                      <p className="text-xs text-emerald-600">{areaData.fmrArea}</p>
                      <p className="text-xs text-emerald-500 mt-1">
                        Area Median Income: {fmt(areaData.medianFamilyIncome)} (family of 4) • {areaData.fiscalYear}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-800 mb-1">Household Information</h2>
                  <p className="text-sm text-slate-500">Tell us about your household to check which programs you may qualify for.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Household Size</label>
                  <p className="text-xs text-slate-400 mb-2">Total number of people who will live in the unit (including children)</p>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <button
                        key={n}
                        onClick={() => setHouseholdSize(n)}
                        className={`py-2.5 rounded-lg text-sm font-semibold transition-all
                          ${householdSize === n 
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                            : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Total Annual Gross Household Income</label>
                  <p className="text-xs text-slate-400 mb-2">Before taxes — combine income from all household members (wages, benefits, Social Security, etc.)</p>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full pl-8 pr-4 py-3.5 text-lg border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                      placeholder="e.g. 35,000"
                      value={annualIncome}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        setAnnualIncome(raw ? parseInt(raw).toLocaleString() : '');
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && checkEligibility()}
                    />
                  </div>
                </div>

                {/* Income limit reference */}
                {areaData.incomeLimits?.veryLow && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Income Limits for Your Area (Household of {householdSize})</p>
                    <div className="space-y-2 text-sm">
                      {areaData.incomeLimits.extremelyLow && (
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                          <span className="text-slate-500">Extremely Low <span className="text-slate-400">(30% AMI)</span></span>
                          <span className="font-semibold text-slate-700">{fmt(areaData.incomeLimits.extremelyLow[householdSize])}</span>
                        </div>
                      )}
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                        <span className="text-slate-500">Very Low <span className="text-slate-400">(50% AMI)</span></span>
                        <span className="font-semibold text-slate-700">{fmt(areaData.incomeLimits.veryLow[householdSize])}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                        <span className="text-slate-500">Low Income <span className="text-slate-400">(80% AMI)</span></span>
                        <span className="font-semibold text-slate-700">{fmt(areaData.incomeLimits.low[householdSize])}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => setStep(0)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Back</button>
                  <button
                    onClick={checkEligibility}
                    disabled={!annualIncome}
                    className="flex-1 py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20"
                  >
                    Check Eligibility →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 2: Deduction Details ── */}
            {step === 2 && (
              <div className="space-y-6">
                {/* Eligible programs */}
                <div>
                  <h2 className="text-xl font-bold text-slate-800 mb-1">You may qualify for:</h2>
                  <p className="text-sm text-slate-500 mb-4">Based on your income and household size in {areaData?.county}, {areaData?.state}</p>
                  
                  <div className="space-y-2">
                    {eligiblePrograms.map(p => (
                      <div key={p.key} className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <div>
                          <p className="text-sm font-semibold text-emerald-800">{p.name}</p>
                          <p className="text-xs text-emerald-600">
                            Income limit: {p.tier}
                            {p.waitlistStatus === 'closed' && ' • waitlist currently closed'}
                            {p.waitlistStatus === 'lottery' && ' • applications by periodic lottery'}
                          </p>
                          {p.targetPopulation && (
                            <p className="text-xs text-emerald-700/80 mt-0.5">For: {p.targetPopulation}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {referralPrograms.length > 0 && (
                    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                      <p className="text-sm font-semibold text-slate-700 mb-1">Specialized {areaData?.state} programs you may be referred to</p>
                      <p className="text-xs text-slate-500 mb-2">These are not open applications. They serve specific situations and are reached through a caseworker, agency, or housing provider.</p>
                      <ul className="space-y-1">
                        {referralPrograms.map(p => (
                          <li key={p.key} className="text-xs text-slate-600">
                            <span className="font-medium text-slate-700">{p.name}</span>
                            {p.targetPopulation && <span> — {p.targetPopulation}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <hr className="border-slate-100" />

                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Estimate Your Monthly Rent</h3>
                  <p className="text-sm text-slate-500">These details help us calculate your Total Tenant Payment (TTP) — the amount you'd pay toward rent.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Dependents Under 18</label>
                  <p className="text-xs text-slate-400 mb-2">Each dependent reduces your adjusted income by $480/year</p>
                  <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                    {[0,1,2,3,4,5,6,7].map(n => (
                      <button
                        key={n}
                        onClick={() => setDependents(n)}
                        className={`py-2 rounded-lg text-sm font-semibold transition-all
                          ${dependents === n ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Is the head of household or spouse elderly (62+) or disabled?</label>
                  <p className="text-xs text-slate-400 mb-2">Qualifies for $525/year deduction + medical expense deduction</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setIsElderlyDisabled(true)}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all
                        ${isElderlyDisabled ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                    >Yes</button>
                    <button
                      onClick={() => setIsElderlyDisabled(false)}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all
                        ${!isElderlyDisabled ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                    >No</button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Annual Childcare Expenses</label>
                  <p className="text-xs text-slate-400 mb-2">Costs that enable a household member to work or attend school</p>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="text" inputMode="numeric"
                      className="w-full pl-8 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                      placeholder="0"
                      value={childcare}
                      onChange={(e) => setChildcare(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </div>
                </div>

                {isElderlyDisabled && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Annual Medical Expenses</label>
                    <p className="text-xs text-slate-400 mb-2">Unreimbursed expenses over 3% of gross income are deducted (elderly/disabled only)</p>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                      <input
                        type="text" inputMode="numeric"
                        className="w-full pl-8 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                        placeholder="0"
                        value={medicalExp}
                        onChange={(e) => setMedicalExp(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Annual Disability Assistance Expenses</label>
                  <p className="text-xs text-slate-400 mb-2">Costs that enable a disabled household member to work</p>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="text" inputMode="numeric"
                      className="w-full pl-8 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                      placeholder="0"
                      value={disabilityExp}
                      onChange={(e) => setDisabilityExp(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Back</button>
                  <button
                    onClick={calculateTTP}
                    className="flex-1 py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
                  >
                    Calculate My Rent →
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Results ── */}
            {step === 3 && result && (
              <div className="space-y-6">
                {result.notEligible ? (
                  /* Not eligible */
                  <div className="text-center py-8">
                    <div className="flex justify-center mb-4"><Frown className="h-12 w-12 text-slate-400" /></div>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Based on the information provided</h2>
                    <p className="text-slate-500 max-w-md mx-auto mb-6">
                      Your household income exceeds the current limits for federal and state housing assistance programs in {areaData?.county}, {areaData?.state}.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
                      <p className="text-sm text-amber-800">
                        <strong>Keep in mind:</strong> Income limits are updated annually (usually in April/May). 
                        Some local programs may have different limits. Contact your local housing authority for the most current information.
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Eligible — show TTP */
                  <>
                    {/* Hero result */}
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 sm:p-8 text-center text-white shadow-xl shadow-blue-600/20">
                      <p className="text-blue-100 text-sm font-medium mb-1">Your Estimated Monthly Payment</p>
                      <p className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-1">{fmtMonthly(result.ttp)}</p>
                      <p className="text-blue-200 text-sm">Total Tenant Payment (TTP) per month</p>
                      
                      <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap justify-center gap-2">
                        {result.programs.map(p => (
                          <span key={p.key} className="bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium">
                            ✓ {p.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* What is TTP */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                      <p className="text-sm text-blue-800">
                        <strong>What is TTP?</strong> Your Total Tenant Payment is the minimum amount you pay toward rent and utilities, regardless of where you live. 
                        For Section 8, your actual rent to the landlord may differ based on the rent charged for the unit, utility allowances, and the payment standard set by your local housing authority.
                      </p>
                    </div>

                    {/* Breakdown */}
                    <div className="border border-slate-100 rounded-xl divide-y divide-slate-100 overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Calculation Breakdown</p>
                      </div>
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                        <span className="text-slate-500">Gross Annual Income</span>
                        <span className="font-semibold text-slate-700 shrink-0">{fmt(result.grossAnnual)}</span>
                      </div>
                      {result.totalDeductions > 0 && (
                        <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                          <span className="text-slate-500">Total Deductions</span>
                          <span className="font-semibold text-red-600 shrink-0">- {fmt(result.totalDeductions)}</span>
                        </div>
                      )}
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                        <span className="text-slate-500">Adjusted Annual Income</span>
                        <span className="font-semibold text-slate-700 shrink-0">{fmt(result.adjustedAnnual)}</span>
                      </div>
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm bg-slate-50">
                        <span className="text-slate-500">30% of Adj. Monthly</span>
                        <span className="font-semibold text-slate-700 shrink-0">{fmtMonthly(result.monthlyAdjusted * 0.3)}</span>
                      </div>
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                        <span className="text-slate-500">10% of Gross Monthly</span>
                        <span className="font-semibold text-slate-700 shrink-0">{fmtMonthly(result.monthlyGross * 0.1)}</span>
                      </div>
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm bg-slate-50">
                        <span className="text-slate-500">Minimum Rent</span>
                        <span className="font-semibold text-slate-700 shrink-0">$50</span>
                      </div>
                      <div className="px-4 py-3 flex justify-between items-baseline gap-3 bg-blue-50">
                        <span className="text-blue-800 font-bold text-sm">TTP (highest of above)</span>
                        <span className="text-blue-800 font-extrabold shrink-0">{fmtMonthly(result.ttp)}/mo</span>
                      </div>
                    </div>

                    {/* Apply CTAs */}
                    {result.programs.some(p => p.applyUrl) && (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-slate-700">Ready to apply?</p>
                        {result.programs.filter(p => p.applyUrl).map(p => (
                          <a
                            key={p.key}
                            href={p.applyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full py-3 text-center bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
                          >
                            Apply for {p.name} →
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Referral-only state programs */}
                    {referralPrograms.length > 0 && (
                      <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                        <p className="text-sm font-semibold text-slate-700">Specialized {areaData?.state} programs (by referral)</p>
                        <p className="text-xs text-slate-500 mb-3">Your income fits these, but each serves a specific situation and is reached through an agency or caseworker rather than a direct application. Ask your housing authority or case manager whether one applies to you.</p>
                        <div className="space-y-2">
                          {referralPrograms.map(p => (
                            <div key={p.key} className="text-xs">
                              <p className="font-medium text-slate-700">
                                {p.name}
                                {p.applyUrl && (
                                  <a href={p.applyUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 font-normal">Learn more →</a>
                                )}
                              </p>
                              {p.targetPopulation && <p className="text-slate-500">{p.targetPopulation}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Nearby Housing Authorities */}
                    <div className="border border-slate-100 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Nearby Housing Authorities</p>
                          <p className="text-xs text-slate-400 mt-0.5">Apply to one or more PHAs for Section 8 or Public Housing</p>
                        </div>
                        {phaLoading && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
                      </div>
                      
                      {nearbyPHAs.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {nearbyPHAs.map((pha) => (
                            <div key={pha.phaCode} className="px-4 py-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start gap-2 flex-wrap">
                                    <p className="text-sm font-semibold text-slate-800">{pha.name}</p>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0
                                      ${pha.programType === 'combined' ? 'bg-purple-100 text-purple-700' :
                                        pha.programType === 'section8' ? 'bg-blue-100 text-blue-700' :
                                        'bg-green-100 text-green-700'}`}>
                                      {pha.programType === 'combined' ? 'PH + S8' :
                                       pha.programType === 'section8' ? 'Section 8' : 'Public Housing'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    {pha.address.city}, {pha.address.state} • {pha.distanceMiles} mi
                                    {pha.totalUnits ? ` • ${pha.totalUnits.toLocaleString()} units` : ''}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                    {pha.phone && (
                                      <a href={`tel:${pha.phone}`} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                                        📞 {pha.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                                      </a>
                                    )}
                                    {pha.email && (
                                      <a href={`mailto:${pha.email}`} className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate max-w-[200px]">
                                        ✉ {pha.email}
                                      </a>
                                    )}
                                    {pha.website && (
                                      <a href={pha.website.startsWith('http') ? pha.website : `https://${pha.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                                        🌐 Website
                                      </a>
                                    )}
                                  </div>
                                  {/* Waitlist status + wait times */}
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                    {pha.statusPH && (
                                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                        pha.statusPH === 'Open' ? 'bg-emerald-100 text-emerald-700' :
                                        pha.statusPH === 'Closed' ? 'bg-red-100 text-red-700' :
                                        'bg-amber-100 text-amber-700'}`}>
                                        {pha.statusPH === 'Open' ? '🟢' : pha.statusPH === 'Closed' ? '🔴' : '🟡'} PH: {pha.statusPH}
                                        {pha.waitMonthsPH ? ` • average wait ~${pha.waitMonthsPH}mo` : ''}
                                      </span>
                                    )}
                                    {pha.statusS8 && (
                                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                        pha.statusS8 === 'Open' ? 'bg-emerald-100 text-emerald-700' :
                                        pha.statusS8 === 'Closed' ? 'bg-red-100 text-red-700' :
                                        'bg-amber-100 text-amber-700'}`}>
                                        {pha.statusS8 === 'Open' ? '🟢' : pha.statusS8 === 'Closed' ? '🔴' : '🟡'} S8: {pha.statusS8}
                                        {pha.waitMonthsS8 ? ` • average wait ~${pha.waitMonthsS8}mo` : ''}
                                      </span>
                                    )}
                                    {pha.hcvCount && (
                                      <span className="text-[10px] text-slate-500">{pha.hcvCount.toLocaleString()} Section 8 vouchers</span>
                                    )}
                                  </div>
                                  {/* Apply link */}
                                  {pha.applyLink && (
                                    <a href={pha.applyLink} target="_blank" rel="noopener noreferrer"
                                      className="inline-block mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800">
                                      Apply Online →
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : !phaLoading ? (
                        <div className="px-4 py-4 text-center">
                          <p className="text-sm text-slate-500">
                            Couldn't load nearby PHAs.{' '}
                            <a href="https://www.hud.gov/program_offices/public_indian_housing/pha/contacts" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">
                              Search on HUD.gov →
                            </a>
                          </p>
                        </div>
                      ) : null}
                      
                      <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100">
                        <a href="https://www.hud.gov/program_offices/public_indian_housing/pha/contacts" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-blue-600 font-medium">
                          View full HUD PHA directory →
                        </a>
                      </div>
                    </div>
                  </>
                )}

                {/* Disclaimer */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <AlertTriangle className="h-3.5 w-3.5 text-slate-400 inline mr-1" /><strong>Disclaimer:</strong> This is an estimate only based on {areaData?.fiscalYear} HUD income limits. 
                    Actual eligibility and rent are determined by your local housing authority based on verified income, deductions, and program-specific rules.
                    Utility allowances, payment standards, and minimum rents vary by housing authority. 
                    This calculator does not guarantee eligibility for any program.
                    Waitlist status and wait times are approximate, based on publicly available data, and may not reflect current conditions.
                    Contact your local housing authority for an official determination.
                  </p>
                </div>

                {/* Restart */}
                <div className="flex gap-3">
                  {!result.notEligible && (
                    <button onClick={() => setStep(2)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Edit Details</button>
                  )}
                  <button
                    onClick={reset}
                    className="flex-1 py-3 bg-slate-100 text-slate-600 font-semibold rounded-xl hover:bg-slate-200 transition-all"
                  >
                    Start New Calculation
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

            </div>{/* end max-w-2xl wizard card */}

            {/* Bottom info section — full width within max-w-5xl */}
            <div className="mt-12 sm:mt-16 grid sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="mb-2"><Landmark className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600 mx-auto" /></div>
                <h3 className="font-bold text-slate-700 text-sm mb-1">Official HUD Data</h3>
                <p className="text-xs text-slate-500">Income limits sourced directly from the U.S. Department of Housing and Urban Development.</p>
              </div>
              <div className="text-center">
                <div className="mb-2"><Lock className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600 mx-auto" /></div>
                <h3 className="font-bold text-slate-700 text-sm mb-1">Private & Anonymous</h3>
                <p className="text-xs text-slate-500">No personally identifiable information is collected — your name and exact income are never stored or shared.</p>
              </div>
              <div className="text-center">
                <div className="mb-2"><Flag className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600 mx-auto" /></div>
                <h3 className="font-bold text-slate-700 text-sm mb-1">Nationwide Coverage</h3>
                <p className="text-xs text-slate-500">Works for every ZIP code in all 50 states, DC, and US territories.</p>
              </div>
            </div>

            {/* For Housing Authorities CTA */}
            <div className="mt-8 sm:mt-12 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 sm:p-8 text-center">
              <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">For Housing Authorities</p>
              <h3 className="text-base sm:text-lg font-bold text-white mb-2">Want this calculator on your website?</h3>
              <p className="text-slate-300 text-xs sm:text-sm mb-4 max-w-md mx-auto">
                Embed a customized version with your branding, utility allowances, and direct application links. 
                Reduce phone calls and help tenants self-serve in 11 languages.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-white text-slate-800 font-semibold px-6 py-3 rounded-xl hover:bg-slate-100 transition-all"
              >
                Learn More About HousingAI →
              </a>
            </div>

          </div>{/* end max-w-5xl */}
        </div>{/* end container */}
      </section>
    </div>
  );
}